import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'

import { useMutation } from '@percent/workplace-giving/common/hooks/useMutation/useMutation'
import { DonationForm, DonationFormProps } from '@percent/workplace-giving/common/components'
import {
  getAccountConfigFromAuthState,
  getPartnerFromAuthState,
  shouldCheckLoggedIn
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { useAuth } from '@percent/workplace-giving/common/hooks'
import { createDonationSession } from '@percent/workplace-giving/api/donation/create-donation-session/create-donation-session'
import { createFundraiserDonationSession } from '@percent/workplace-giving/api/donation/create-fundraiser-donation-session/create-fundraiser-donation-session'

type DonationType = 'regular' | 'fundraiser'

export const useDonationForm = (type: DonationType) => {
  const { state, dispatch, logout } = useAuth()
  const partner = getPartnerFromAuthState(state)!
  const accountConfig = getAccountConfigFromAuthState(state)
  const {
    i18n: { language: locale }
  } = useTranslation()
  const { pathname, search } = useLocation()
  const redirectUrl = `${window.location.origin}${pathname}${search}`

  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')

  const regularMutation = useMutation({
    mutationFn: createDonationSession
  })

  const fundraiserMutation = useMutation({
    mutationFn: createFundraiserDonationSession
  })

  // Select the appropriate mutation based on type
  const { isLoading } = type === 'fundraiser' ? fundraiserMutation : regularMutation

  const handleClose = useCallback(() => {
    setOpen(false)
    setUrl('')
  }, [])

  const handleDonate = useCallback(
    async (id: string) => {
      if (!shouldCheckLoggedIn()) {
        logout()

        return
      }

      if (accountConfig?.currency || partner.currencyCode) {
        try {
          const response =
            type === 'fundraiser'
              ? await fundraiserMutation.mutateAsync({
                  donationLinkReference: id,
                  currency: accountConfig?.currency ?? partner.currencyCode,
                  language: locale
                })
              : await regularMutation.mutateAsync({
                  organisationId: id,
                  currency: accountConfig?.currency ?? partner.currencyCode
                })

          setUrl(response.data.url)
          setOpen(true)
        } catch (error) {
          Sentry.captureException(error)
        }
      }
    },
    [accountConfig?.currency, partner.currencyCode, regularMutation, fundraiserMutation, type, dispatch, locale, logout]
  )
  const DonationFormWrapper = useCallback(
    (props: Omit<DonationFormProps, 'loading' | 'open' | 'url' | 'onClose' | 'redirectUrl'>) => (
      <DonationForm
        {...props}
        loading={isLoading}
        open={open}
        url={url}
        onClose={handleClose}
        redirectUrl={redirectUrl}
      />
    ),
    [isLoading, open, url, handleClose, redirectUrl]
  )

  return useMemo(
    () => ({
      DonationFormWrapper,
      handleDonate
    }),
    [DonationFormWrapper, handleDonate]
  )
}
